import { useState } from "react";
import type { FormEvent } from "react";
import { categories } from "@/mocks/positiveNews";

const FORM_URL = "https://readdy.ai/api/form/dae1dhptto0ceqqngnn0";

const steps = [
  { n: "01", icon: "ri-quill-pen-line", title: "填写线索", text: "留下称呼、栏目与简要经过。" },
  { n: "02", icon: "ri-file-search-line", title: "编辑核实", text: "我们会对内容与来源认真核实。" },
  { n: "03", icon: "ri-store-3-line", title: "收录发布", text: "通过后按栏目收录并注明来源。" },
];

export default function ContactSection() {
  const [status, setStatus] = useState<{ kind: "idle" | "success" | "error"; msg: string }>({ kind: "idle", msg: "" });
  const [len, setLen] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 反垃圾：命中隐藏域则按正常成功反馈处理，但不发送
    const honeypot = String(data.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus({ kind: "success", msg: "感谢投稿！我们会在核实后尽快与你联系。" });
      form.reset();
      setLen(0);
      return;
    }

    data.delete("website_alt");
    setSubmitting(true);
    setStatus({ kind: "idle", msg: "" });
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const okCode = parsed?.code === "OK" || (!parsed && res.ok);
      if (res.ok && okCode && !/spam/i.test(serverMsg)) {
        setStatus({ kind: "success", msg: "感谢投稿！我们会在核实后尽快与你联系。" });
        form.reset();
        setLen(0);
      } else {
        setStatus({ kind: "error", msg: serverMsg && serverMsg !== responseText ? serverMsg : "提交失败，请稍后再试。" });
      }
    } catch {
      setStatus({ kind: "error", msg: "网络异常，请稍后再试。" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="contact" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5 lg:gap-12">
          {/* 左侧说明与流程 */}
          <div className="lg:col-span-2">
            <p className="mb-2 flex items-center gap-2 text-xs font-medium tracking-widest text-primary-600">
              <span className="h-px w-6 bg-primary-400" />
              投稿 / 联系 · CONTACT
            </p>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">身边有温暖，别让它悄悄溜走</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              无论是你亲眼所见的好人好事，还是你参与的公益行动，都欢迎告诉我们。让更多人看到，善意就多一份被放大的可能。
            </p>

            <div className="mt-7 flex flex-col gap-4">
              {steps.map((s) => (
                <div key={s.n} className="flex items-start gap-4">
                  <span className="font-heading flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-2xl bg-primary-500 text-background-50">
                    <i className={`${s.icon} text-lg`} />
                    <span className="text-[10px] leading-none">{s.n}</span>
                  </span>
                  <div className="border-b border-background-200 pb-4">
                    <h3 className="text-sm font-bold text-foreground-950 md:text-base">{s.title}</h3>
                    <p className="mt-1 text-sm text-foreground-600">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-background-200 bg-background-50 p-5">
              <p className="text-sm font-bold text-foreground-950">其他联系方式</p>
              <a
                href="mailto:hello@example.com"
                className="mt-3 inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-mail-send-line" />
                hello@example.com
              </a>
              <p className="mt-3 flex items-start gap-2 text-sm text-foreground-600">
                <i className="ri-time-line mt-0.5" />
                常规工作日回复，投稿量大时可能需要稍候，请耐心等待。
              </p>
            </div>
          </div>

          {/* 右侧表单 */}
          <div className="lg:col-span-3">
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              noValidate
              className="rounded-3xl border border-background-200 bg-background-50 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-2 block text-sm font-medium text-foreground-800">
                    您的称呼 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    maxLength={30}
                    placeholder="如何称呼您"
                    className="w-full rounded-lg border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground-800">
                    联系邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    maxLength={80}
                    placeholder="用于反馈投稿结果"
                    className="w-full rounded-lg border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="category" className="mb-2 block text-sm font-medium text-foreground-800">
                    投稿栏目
                  </label>
                  <select
                    id="category"
                    name="category"
                    defaultValue=""
                    className="w-full cursor-pointer rounded-lg border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="" disabled>
                      请选择栏目
                    </option>
                    {categories.map((c) => (
                      <option key={c.key} value={c.label}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <div className="mb-2 flex items-center justify-between">
                    <label htmlFor="story" className="block text-sm font-medium text-foreground-800">
                      故事经过 / 线索 <span className="text-primary-600">*</span>
                    </label>
                    <span className="text-xs text-foreground-500">{len}/500</span>
                  </div>
                  <textarea
                    id="story"
                    name="story"
                    required
                    rows={6}
                    maxLength={500}
                    onChange={(e) => setLen(e.target.value.length)}
                    placeholder="请简要描述事件经过、时间地点，若有多张图片可在投稿审核通过后另行提供。请勿填写他人完整姓名与住址等敏感信息。"
                    className="w-full resize-none rounded-lg border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
              </div>

              {/* 反垃圾隐藏域 */}
              <div className="field-note" aria-hidden="true">
                <label htmlFor="website_alt">网站地址</label>
                <input
                  id="website_alt"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              {status.kind !== "idle" && (
                <div
                  className={`mt-4 flex items-start gap-2 rounded-lg px-4 py-3 text-sm ${
                    status.kind === "success"
                      ? "bg-secondary-100 text-secondary-900"
                      : "bg-primary-100 text-primary-900"
                  }`}
                >
                  <i className={`mt-0.5 ${status.kind === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"}`} />
                  <span>{status.msg}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    正在提交…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill" />
                    提交投稿
                  </>
                )}
              </button>
              <p className="mt-3 text-xs text-foreground-500">提交即表示你同意内容真实可核，且不侵犯他人隐私与版权。</p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}